<template>
  <v-dialog :value="value" persistent max-width="750px">
    <v-card width="1000">
      <v-card-title>
        <span>Confirm Linked Group Update</span>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="items"
        :height="500"
        :loading="loading"
        :items-per-page="-1"
        v-model="selectedItems"
        show-select
        hide-default-footer>
        <template #item.new_multiplier>
          <td class="noWrap">{{ newMultiplier }}</td>
        </template>
        <template #item.new_srp>
          <td class="noWrap">${{ newSrp | format }}</td>
        </template>
      </v-data-table>
      <v-divider></v-divider>
      <v-card-actions flat>
        <v-spacer />
        <v-btn
          text
          @click="closeModal">
          Cancel
        </v-btn>
        <v-btn 
          class="blue white--text mr-2"
          @click="updateItems" :disabled="loading">
          Save Selected Items
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import Items from '@/axios/items';
import Rules from '@/axios/rules-management'
import { shared } from '@/mixins/shared'
import { utils } from '@/mixins/utils'
export default {
  name: 'LinkedGroupModal',
  mixins: [shared, utils],
  props: {
    value: { type: Boolean, default: false },
    item: { type: Object, default: () => ({}) },
    batch: { type: Object, default: () => ({}) },
    batchItems: { type: Array, default: () => ([]) },
    store: { type: Object, default: () => ({}) },
    isTprProRetailUser: { type: Boolean, default: false },
    storeGroupIsSelected: { type: Boolean, default: false },
    propagateChangesToChildRows: { type: Boolean, default: false }
  },
  data() {
    return {
      batchId: null,
      items: [],
      linkedGroup: null,
      loading: false,
      newMultiplier: null,
      newSrp: null,
      selectedItems: [],
      storeId: null,
      headers: [
        { value: 'data-table-select', class: 'accent white--text', align: "center" },
        { text: "Item ID", value: "item_id", class: "accent white--text", align: "start" },
        { text: "UPC", value: "upc", class: "accent white--text", align: "start" },
        { text: "Description", value: "description", class: "accent white--text", align: "start" },
        { text: "Multiplier", value: "new_multiplier", class: "accent white--text", align: "start" },
        { text: "SRP", value: "new_srp", class: "accent white--text", align: "start" }
      ]
    }
  },
  async created() {
    this.batchId = this.batch.id
    this.linkedGroup = this.item.linked_group
    if (this.isTprProRetailUser) {
      this.storeId = this.store?.store_party_id || this.store?.party_id || this.item?.store_id || this.item?.store_party_id
      this.batchId = this.batch.tpr_batch_id
    }
    this.newMultiplier = this.item.multiplier
    this.newSrp = this.item.promotion_price
    this.getLinkedItems()
  },
  methods: {
    async getLinkedItems() {
      const payload = {
        linked_group: [this.linkedGroup],
        tpr_date: this.batch.tpr_date
      }
      if (this.isTprProRetailUser) payload.store_ids = [this.storeId]
      try {
        this.loading = true
        const { data } = await Items.getItems(this.batchId, payload, 500, 0)
        this.items = data
        this.selectedItems = this.items
        this.items.forEach((item) => {
          item.net_gross_profit = this.calculateGrossProfit(item)
        })
      } catch (err) {
        console.error("Error retrieving linked group items")
        const message = err?.response?.data?.message || err.message
        this.alert(`Error retrieving items: ${message}`)
      } finally {
        this.loading = false
      }
    },
    calculateGrossProfit(item) {
      if (this.newMultiplier <= 0 || this.newSrp <= 0) {
        // Return 0.00% for invalid inputs
        return 0.00;
      }
      let allowances = Number(item.amap_allowance) + Number(item.dsd_oi_allowance) + Number(item.billback_allowance) + Number(item.tpr_billback_allowance) + Number(item.edlc_billback_allowance) + Number(item.vpap_tpr_billback_allowance) + Number(item.accrual_tpr_billback_allowance)
      // Exclude Early Buy Allowance from calculation for ARG
      if (this.$auth.tenant != 'alliance-retail-group') {
        allowances += Number(item.early_buy_allowance)
      }
      let scanAllowances = Number(item.scan_allowance) + Number(item.edlc_scan_allowance) + Number(item.tpr_scan_allowance)
      item.nonCompliantList = []
      // Map promo categories to the corresponding allowance column
      const catToCol = {
        "TPR Scan - ePay": "tpr_scan_epay_allowance",
        "TPR Scan - Compete": "tpr_scan_compete_allowance",
        "EDLC Scan - ePay": "edlc_scan_epay_allowance",
        "EDLC Scan - Compete": "edlc_scan_compete_allowance"
      }
      if (this.isTprProRetailUser) {
        item.enforce_compliance_promos?.forEach((promo) => {
          let col = catToCol[promo.promo_category_name]
          if (this.newSrp <= (promo.promotion_price + (item.variance ?? 0))) {
            scanAllowances += item[col] ?? 0
          }
          else {
            item.nonCompliantList.push(promo.promo_category_name)
          }
        })
      } else {
        scanAllowances += Number(item.tpr_scan_compete_allowance) + Number(item.tpr_scan_epay_allowance) + Number(item.edlc_scan_compete_allowance) + Number(item.edlc_scan_epay_allowance)
      } 
      let cost_source = item.cost
      if (this.tprProTenants && item.case_freight != null) {
        cost_source = item.case_freight
      }
      const unitCost = ((Number(cost_source) - allowances) / Number(item.qty_per_case)) - scanAllowances
      const grossProfit = (((Number(this.newSrp) / Number(this.newMultiplier)) - unitCost) / (Number(this.newSrp) / Number(this.newMultiplier))) * 10000
      return Math.round(grossProfit) / 100
    },
    async updateItems() {
      this.loading = true
      const differingAltItems = []
      const differentStoreItems = this.selectedItems.filter(item => {
        const itemDiffers = Number(item.promotion_price) != Number(this.newSrp) || Number(item.multiplier) !== Number(this.newMultiplier)
        const itemAltItems = this.batchItems.find(bi => bi.id === item.id)
        // Cascade mode: always persist children (the in-memory diff can't tell saved from propagated).
        if (this.propagateChangesToChildRows && itemAltItems?.alternate_store_items?.length > 0) {
          itemAltItems.alternate_store_items.forEach(altItem => {
            altItem.net_gross_profit = this.calculateGrossProfit(altItem)
            differingAltItems.push(altItem)
          })
        }
        return itemDiffers
      })
      const differingItems = [...differentStoreItems, ...differingAltItems]
      if (differingItems.length <= 0) {
        this.notify("SRP and Multiplier is already set to given values for each item in Linked Group")
        this.closeModal()
        return
      }
      const payload = differingItems.map(item => {
        return {
          id: item.id,
          promotion_price: this.newSrp,
          multiplier: this.newMultiplier,
          note: item.note,
          net_gross_profit: item.net_gross_profit
        }
      })
      try {
        if (this.isTprProRetailUser) {
          const { data } = await Rules.storeSrpMultiplier(payload)
          if (data.message === 'TPR successfully updated.') {
            this.notify("Linked Group items have been updated successfully")
          } else {
            this.alert("Linked Group items were not updated.")
          }
        } else {
          const { data } = await Items.tprUpdate(payload)
          if (data.updated > 0) {
            this.notify("Linked Group items have been updated successfully")
          } else {
            this.alert("Linked Group items were not updated.")
          }
        }
        const updatedDifferingItems = differingItems.map(item => {
          return {
            ...item,
            promotion_price: this.newSrp,
            multiplier: this.newMultiplier
          }
        })
        this.$emit('onSave', updatedDifferingItems)
      } catch (err) {
        console.error("Error updating items:")
        const message = err?.response?.data?.message || err.message
        this.alert(`Error updating items: ${message}`)
      } finally {
        this.closeModal()
      }
    },
    closeModal() {
      this.newMultiplier = null
      this.newSrp = null
      this.items = []
      this.selectedItems = []
      this.$emit("update:value", false)
    },
  },
}
</script>